import moment from "moment";

import { formatE164, getDateWithoutTime } from "helpers";
import {
	backendPlatformsWithFrontendPlatforms,
	mapAddressToApi,
} from "mappers";
import {
	BackEndPlatformTypes,
	FrontEndPlatformTypes,
	TerminalProductNameTypes,
	TerminalSolutionTypes,
	ShippingMethodTypes,
	TerminalConfigurationOption,
	ZeroCostProgramTypes,
	BillToTypes,
	TerminalGatewayTypes,
} from "types";
import {
	TerminalOption,
	TerminalSolutionForAppDto,
	TerminalSolutionGatewayDto,
	TerminalSolutionPointOfSaleDto,
} from "types/api";

export const getFrontEndPlatform = values => {
	const frontEndPlatforms: any[] = [];

	const hasFrontEndPlatform = backendPlatformsWithFrontendPlatforms.includes(
		values?.backEndPlatform,
	);

	values.omahaFrontEndPlatform &&
		frontEndPlatforms.push(FrontEndPlatformTypes.Omaha.value);
	values.northFrontEndPlatform &&
		frontEndPlatforms.push(FrontEndPlatformTypes.North.value);
	values.nashvilleFrontEndPlatform &&
		frontEndPlatforms.push(FrontEndPlatformTypes.Nashville.value);
	values.compassFrontEndPlatform &&
		frontEndPlatforms.push(FrontEndPlatformTypes.Compass.value);
	values.buypassFrontEndPlatform &&
		frontEndPlatforms.push(FrontEndPlatformTypes.BuyPass.value);
	values.otherFrontEndPlatform &&
		frontEndPlatforms.push(FrontEndPlatformTypes.Other.value);

	return hasFrontEndPlatform
		? {
				front_end_platforms:
					frontEndPlatforms.length > 0
						? frontEndPlatforms
						: undefined,
				other_front_end_platform_name:
					values?.otherFrontEndPlatformName,
		  }
		: {
				front_end_platforms: undefined,
				other_front_end_platform_name: undefined,
		  };
};

export const getBackEndPlatform = values => {
	return {
		back_end_platform: values?.backEndPlatform,
		other_back_end_platform_name: values?.otherBackEndPlatformName,
	};
};

export const getPaymentMethods = paymentMethods => {
	return {
		billings: paymentMethods?.map(item => ({
			id: item?.id,
			bill_to: item?.billToInfo.billTo,
			amount_to_be_billed: item?.billToInfo?.amountToBeBilled || 0,
			billing_status: item?.paymentTypeInfo?.billingStatus?.name,
			invoice_date: item?.paymentTypeInfo?.invoiceDate
				? getDateWithoutTime(item?.paymentTypeInfo?.invoiceDate)
				: "",
			payment_date: item?.paymentTypeInfo?.paymentDate
				? getDateWithoutTime(item?.paymentTypeInfo?.paymentDate)
				: "",
			payment_method_id: item?.paymentMethodId || null,
		})),
	};
};

export const getShippingData = shipping => {
	const addressData = shipping?.address
		? mapAddressToApi({ address: shipping?.address })
		: undefined;

	return {
		shipping_method: shipping?.shippingMethod,
		address: addressData || undefined,
		phone_number: formatE164(shipping?.phoneNumber),
	};
};

// used for create/update Merchant terminal
export const getShippingDetails = shipping => {
	return {
		order_number: shipping?.orderNumber,
		vendor: shipping?.vendor,
		carrier: shipping?.carrier,
		tracking_number: shipping?.trackingNumber,
		ordered_at: getDateWithoutTime(shipping?.orderedAt),
		shipped_at: shipping?.shippedAt
			? getDateWithoutTime(shipping?.shippedAt)
			: "",
		delivered_at: getDateWithoutTime(shipping?.deliveredAt),
	};
};

export const getFileBuildInfoForMerchant = (fileBuild, backEndPlatform) => {
	return {
		file_build: {
			file_build_option: fileBuild?.fileBuildOption,
			file_type: fileBuild?.fileType,
			dlid: fileBuild?.dlid,
			datawire_id: fileBuild?.datawireId,
			tsys_terminal_settings:
				backEndPlatform === BackEndPlatformTypes.Tsys.name
					? {
							store_number:
								fileBuild?.tsysTerminalSettings?.storeNumber,
							terminal_number:
								fileBuild?.tsysTerminalSettings?.terminalNumber,
							send_email:
								fileBuild?.tsysTerminalSettings?.sendEmail,
							device_id:
								fileBuild?.tsysTerminalSettings?.deviceId,
							user_id: fileBuild?.tsysTerminalSettings?.userId,
							password: fileBuild?.tsysTerminalSettings?.password,
					  }
					: undefined,
		},
	};
};

export const getInstallationInfo = installation => {
	return {
		installation: installation
			? {
					terminal_install: installation?.terminalInstall,
					best_time_to_call: installation?.bestTimeToCall,
					contact_first_name: installation?.contactFirstName,
					contact_last_name: installation?.contactLastName,
					contact_phone_number: formatE164(
						installation?.contactPhoneNumber,
					),
					contact_email: installation?.contactEmail,
					notes: installation?.notes,
					uninstall_at: installation?.uninstallAt
						? getDateWithoutTime(installation?.uninstallAt)
						: "",
					install_at: installation?.installAt
						? getDateWithoutTime(installation?.installAt)
						: "",
			  }
			: undefined,
	};
};

export const getZeroCostInfo = values => {
	const zeroCostAmount =
		values?.zeroCostProgramType ===
		ZeroCostProgramTypes.None.value?.toString()
			? null
			: parseFloat(values?.zeroCostAmount);
	return {
		zero_cost_program_type: parseInt(values?.zeroCostProgramType),
		zero_cost_amount: zeroCostAmount,
	};
};

export const getFileBuildInfoForApp = fileBuild => {
	return {
		file_build: {
			file_build_option: fileBuild?.fileBuildOption,
			file_type: fileBuild?.fileType,
		},
	};
};

export const getTerminalOptions = options => {
	const terminalOptions: TerminalOption[] = [];
	options.avs && terminalOptions.push("avs");
	options.cvv && terminalOptions.push("cvv");
	options.multiMerchant && terminalOptions.push("multi_merchant");
	options.tipAdjust && terminalOptions.push("tip_adjust");
	options.serverNumber && terminalOptions.push("server_number");
	options.orderNumber && terminalOptions.push("order_number");
	options.amex && terminalOptions.push("amex");
	options.pinDebit && terminalOptions.push("pin_debit");
	options.externalPinPad && terminalOptions.push("external_pin_pad");

	return {
		terminal_options: terminalOptions,
	};
};

export const getAutoCloseAt = values => {
	const autoCloseAt =
		values?.isAutoCloseEnabled &&
		values?.autoCloseAtHours &&
		values?.autoCloseAtMinutes &&
		values?.autoCloseAtMedian?.value &&
		moment(
			`${values?.autoCloseAtHours}:${values?.autoCloseAtMinutes} ${values?.autoCloseAtMedian?.value}`,
			"hh:mm A",
		).format("HH:mm:ss");

	return {
		auto_close_at: autoCloseAt,
	};
};

export const getTerminalInfo = values => {
	const isNewRental =
		TerminalConfigurationOption.NewRental.name ===
		values?.terminalConfigurationOption;

	return {
		terminal_configuration_option: values?.terminalConfigurationOption,
		terminal_type: values?.terminalType.value,
		manufacturer_id: values?.manufacturer?.id,
		other_manufacturer_name: values?.otherManufacturerName,
		model_id: values?.model?.id,
		other_model_name: values?.otherModelName,
		terminal_devices: values?.terminalDevices?.map(d => ({
			id: d?.deviceId,
			type_id: d?.typeId,
			serial_number: d?.serialNumber,
		})),
		price_per_terminal: values?.pricePerTerminal || "",
		dvid: values?.dvid || "",

		sim_number: values?.simNumber,
		terminal_accessories: values?.terminalAccessories
			?.filter(a => a.type?.id)
			?.map(a => ({
				id: a.id,
				type: a.type.name,
				type_id: a.type.id,
				other_name: a.otherName,
				price: a.price || null,
				serial_number: a.serialNumber,
			})),

		industry_type: values?.industryType,
		connection_primary: values?.connectionPrimary,
		connection_backup: values?.connectionBackup,
		is_auto_close_enabled: values?.isAutoCloseEnabled,

		clover_terminal_settings: values?.cloverTerminalSettings
			? {
					clover_id: values?.cloverTerminalSettings.cloverId,
					go_monthly_fee:
						values?.cloverTerminalSettings.goMonthlyFee || null,
					services_per_device_fee:
						values?.cloverTerminalSettings.servicesPerDeviceFee ||
						null,
					username: values?.cloverTerminalSettings.username,
			  }
			: undefined,
		is_tsys_tokenization_enabled: values?.isTsysTokenizationEnabled,
		is_transarmor_enabled: values?.isTransarmorEnabled,
		transarmor_per_transaction_fee:
			values?.transarmorPerTransactionFee || null,
		transarmor_monthly_fee: values?.transarmorMonthlyFee || null,
		month_rental_price: isNewRental
			? values?.monthRentalPrice || null
			: undefined,
		equipment_insurance: isNewRental
			? values?.equipmentInsurance || null
			: undefined,
		shipping_and_handling: isNewRental
			? values?.shippingAndHandling || null
			: undefined,
		estimated_tax: isNewRental ? values?.estimatedTax || null : undefined,
		state_tax: isNewRental ? values?.stateTax || null : undefined,
	};
};

export const getGatewayInfo = values => {
	const resetIfArise = value => {
		if (values?.gatewayName?.name === TerminalGatewayTypes.ARISE.name) {
			return null;
		} else {
			return value || null;
		}
	};

	return {
		gateway_configuration_option: values?.gatewayConfigurationOption,
		gateway_id: values?.gatewayId,
		gateway_name_id: values?.gatewayName?.id,
		gateway_name: values?.gatewayName?.name,
		other_gateway_name: values?.otherGatewayName,
		subdomain: values?.subdomain,
		username: values?.username,
		host_password: values?.hostPassword,
		display_name: values?.displayName,
		is_level_2_and_3_data_enabled: values?.isLevel2And3DataEnabled,
		is_cash_discount_enabled: values?.isCashDiscountEnabled,
		is_customer_vault_or_recurring_billing:
			values?.isCustomerVaultOrRecurringBilling,
		monthly_fee: resetIfArise(values?.monthlyFee),
		transaction_fee: resetIfArise(values?.transactionFee),
		level_3_per_transaction_fee: resetIfArise(
			values?.level3PerTransactionFee,
		),
		vault_monthly_fee: resetIfArise(values?.vaultMonthlyFee),
		setup_fee: resetIfArise(values?.setupFee),

		card_network_tokenization_setup_fee:
			values?.cardNetworkTokenizationSetupFee || null,
		card_network_tokenization_monthly_fee:
			values?.cardNetworkTokenizationMonthlyFee || null,
		card_network_per_tokenization_event_fee:
			values?.cardNetworkPerTokenizationEventFee || null,

		is_tsys_tokenization_enabled: values?.isTsysTokenizationEnabled,
		is_transarmor_enabled: values?.isTransarmorEnabled,
		transarmor_per_transaction_fee:
			values?.transarmorPerTransactionFee || null,
		transarmor_monthly_fee: values?.transarmorMonthlyFee || null,
	};
};

export const getPointOfSaleInfo = (values): TerminalSolutionPointOfSaleDto => {
	let pointOfSaleData: TerminalSolutionPointOfSaleDto = {
		subscriptions: null,
		name_if_other: null,
		product: values?.product?.name,
		authorization_version: values?.authorizationVersion,
		is_tsys_tokenization_enabled:
			values?.isTsysTokenizationEnabled === "true",
		is_transarmor_enabled: values?.isTransarmorEnabled === "true",
		transarmor_per_transaction_fee: null,
		transarmor_monthly_fee: null,
		merchant_type: values?.merchantType?.name,
	};

	const productName = values?.product?.name;

	if (productName === TerminalProductNameTypes.Other.name) {
		pointOfSaleData.name_if_other = values?.nameIfOther;
	} else if (
		productName === TerminalProductNameTypes.NailSoft.name ||
		productName === TerminalProductNameTypes.Calendarise.name
	) {
		pointOfSaleData.subscriptions = {
			pos_system: {
				billing_cadence:
					values?.subscriptions?.posSystem?.billingCadence?.name,
				cost: values?.subscriptions?.posSystem?.cost || 0,
				quantity: values?.subscriptions?.posSystem?.quantity,
				is_subscribed: values?.subscriptions?.posSystem?.isSubscribed,
			},
			marketing_services: {
				billing_cadence:
					values?.subscriptions?.marketingServices?.billingCadence
						?.name,
				cost: values?.subscriptions?.marketingServices?.cost || 0,
				quantity: values?.subscriptions?.marketingServices?.quantity,
				is_subscribed:
					values?.subscriptions?.marketingServices?.isSubscribed,
			},
			ip_phone_service: {
				billing_cadence:
					values?.subscriptions?.ipPhoneService?.billingCadence?.name,
				cost: values?.subscriptions?.ipPhoneService?.cost || 0,
				quantity: values?.subscriptions?.ipPhoneService?.quantity,
				is_subscribed:
					values?.subscriptions?.ipPhoneService?.isSubscribed,
			},
			tap_to_pay: {
				billing_cadence:
					values?.subscriptions?.tapToPay?.billingCadence?.name,
				cost: values?.subscriptions?.tapToPay?.cost || 0,
				quantity: values?.subscriptions?.tapToPay?.quantity,
				is_subscribed: values?.subscriptions?.tapToPay?.isSubscribed,
			},
			sms_service: {
				billing_cadence:
					values?.subscriptions?.smsService?.billingCadence?.name,
				cost: values?.subscriptions?.smsService?.cost || 0,
				quantity: values?.subscriptions?.smsService?.quantity,
				is_subscribed: values?.subscriptions?.smsService?.isSubscribed,
			},
			sms_message: {
				billing_cadence:
					values?.subscriptions?.smsMessage?.billingCadence?.name,
				cost: values?.subscriptions?.smsMessage?.cost || 0,
				quantity: values?.subscriptions?.smsMessage?.quantity,
				is_subscribed: values?.subscriptions?.smsMessage?.isSubscribed,
			},
			sms_setup: {
				billing_cadence:
					values?.subscriptions?.smsSetup?.billingCadence?.name,
				cost: values?.subscriptions?.smsSetup?.cost || 0,
				quantity: values?.subscriptions?.smsSetup?.quantity,
				is_subscribed: values?.subscriptions?.smsSetup?.isSubscribed,
			},
			salon_in_the_box: {
				billing_cadence:
					values?.subscriptions?.salonInTheBox?.billingCadence?.name,
				cost: values?.subscriptions?.salonInTheBox?.cost || 0,
				quantity: values?.subscriptions?.salonInTheBox?.quantity,
				is_subscribed:
					values?.subscriptions?.salonInTheBox?.isSubscribed,
			},
			discount: values?.subscriptions?.discount || 0,
		};

		if (values?.subscriptions?.salonInTheBox === null) {
			delete pointOfSaleData.subscriptions.salon_in_the_box;
		}
		if (values?.subscriptions?.marketingServices === null) {
			delete pointOfSaleData.subscriptions.marketing_services;
		}
	}
	if (values?.isTransarmorEnabled === "true") {
		pointOfSaleData.transarmor_per_transaction_fee =
			values?.transarmorPerTransactionFee;
		pointOfSaleData.transarmor_monthly_fee = values?.transarmorMonthlyFee;
	}

	return pointOfSaleData;
};
export const mapSoftwareOptionsGeneralData = values => {
	const zeroCostInfo = getZeroCostInfo(values);
	const installationInfo = getInstallationInfo(values?.installation);

	const shippingData = {
		shipping:
			values?.shipping?.shippingMethod !==
			ShippingMethodTypes.none.value?.toString()
				? {
						...getShippingData(values?.shipping),
						should_copy_shipping_address_to_merchant:
							values?.shipping
								?.shouldCopyShippingAddressToMerchant,
				  }
				: undefined,
	};

	return {
		...installationInfo,
		...shippingData,
		...zeroCostInfo,
	};
};

export const mapSoftwareOptionsFileBuildOnly = values => {
	return {
		file_build_only_quantity: values?.fileBuildOnlyQuantity || "0",
		file_build_only_var_recipient_email:
			values?.fileBuildOnlyVarRecipientEmail || null,
	};
};

export const mapSoftwareBillingInfo = values => {
	const isBillToAurora =
		values?.billTo === BillToTypes.aurora.value?.toString();

	return {
		bill_to: values?.billTo,
		payment_method_id: !isBillToAurora
			? values?.paymentMethodId || null
			: null,
	};
};

export const getTerminalFieldsForApp = values => {
	const getFieldsFn = () => {
		let fileBuild = {};
		let frontEndPlatforms = {};
		let backEndPlatform = {};

		if (isTerminal) {
			fileBuild = getFileBuildInfoForApp(values?.terminal?.fileBuild);
			frontEndPlatforms = getFrontEndPlatform(values?.terminal);
			backEndPlatform = getBackEndPlatform(values?.terminal);
		} else if (isGateway) {
			fileBuild = getFileBuildInfoForApp(values?.gateway?.fileBuild);
			frontEndPlatforms = getFrontEndPlatform(values?.gateway);
			backEndPlatform = getBackEndPlatform(values?.gateway);
		} else if (isPointOfSale) {
			fileBuild = getFileBuildInfoForApp(values?.pointOfSale?.fileBuild);
			frontEndPlatforms = getFrontEndPlatform(values?.pointOfSale);
			backEndPlatform = getBackEndPlatform(values?.pointOfSale);
		}
		return { ...fileBuild, ...frontEndPlatforms, ...backEndPlatform };
	};

	const isTerminal =
		values?.terminalSolutionType === TerminalSolutionTypes.Terminal.name;
	const isGateway =
		values?.terminalSolutionType === TerminalSolutionTypes.Gateway.name;
	const isPointOfSale =
		values?.terminalSolutionType === TerminalSolutionTypes.PointOfSale.name;

	const fields = getFieldsFn();

	const id = values?.id || NaN;

	let data: TerminalSolutionForAppDto = {
		quantity: values?.terminal?.quantity || "1",
		status: "draft",
		is_file_build_only_solution_type: values?.isFileBuildOnly,
		...fields,
	};

	let terminal: any | undefined = undefined;
	let gateway: TerminalSolutionGatewayDto | undefined = undefined;
	let pointOfSale: TerminalSolutionPointOfSaleDto | undefined = undefined;

	if (isTerminal) {
		const terminalOptions = getTerminalOptions(
			values?.terminal?.terminalOptions,
		);
		const autoCloseAt = getAutoCloseAt(values?.terminal);

		const terminalInfo = getTerminalInfo({
			...values?.terminal,
			terminalAccessories: values?.terminalAccessories,
		});

		terminal = {
			...terminalOptions,
			...autoCloseAt,
			...terminalInfo,
		};
	}

	if (isGateway) {
		const gatewayInfo = getGatewayInfo(values?.gateway);
		gateway = {
			...gatewayInfo,
		};
	}

	if (isPointOfSale) {
		const pointOfSaleInfo = getPointOfSaleInfo(values?.pointOfSale);
		pointOfSale = {
			...pointOfSaleInfo,
		};
	}

	return {
		...data,
		id,
		terminal,
		gateway,
		point_of_sale: pointOfSale,
	};
};

export const terminalCalculation = (
	terminalForm: { [key: string]: any }[] | { [key: string]: any },
) => {
	let amountToBeBilled = 0;
	const calculate = (terminal, terminalAccessories) => {
		let terminalAmountToBeBilled = 0;
		const isNewRental =
			terminal?.terminalConfigurationOption ===
			TerminalConfigurationOption.NewRental.name;
		if (isNewRental) {
			terminalAmountToBeBilled +=
				parseFloat(terminal?.monthRentalPrice || 0) +
				parseFloat(terminal?.equipmentInsurance || 0) +
				parseFloat(terminal?.shippingAndHandling || 0) +
				parseFloat(terminal?.estimatedTax || 0);
		} else {
			terminalAmountToBeBilled += parseFloat(
				terminal?.pricePerTerminal || 0,
			);
		}

		terminalAccessories?.forEach(accessory => {
			terminalAmountToBeBilled += parseFloat(accessory.price || 0);
		});

		terminalAmountToBeBilled *= terminal?.quantity || 1;

		return terminalAmountToBeBilled;
	};

	if (Array.isArray(terminalForm)) {
		terminalForm?.forEach(terminalData => {
			amountToBeBilled += calculate(
				terminalData?.terminal,
				terminalData?.terminalAccessories,
			);
		});
	} else {
		amountToBeBilled += calculate(
			terminalForm?.terminal,
			terminalForm?.terminalAccessories,
		);
	}

	return amountToBeBilled;
};
